import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import {
  auth,
  yachts,
  yachtBookings,
  coupons,
  rentals,
  contact,
  drivers,
  partners,
  tours,
  tourBookings,
} from "../services/api";
import CreateTourForm from "../components/TourForm/CreateTourForm";
import EditTourForm from "../components/TourForm/EditTourForm";
import HTMLContent from "../components/common/HtmlContent";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import NotificationSystem from "../components/Notification/NotificationSystem";
import {
  FaShip,
  FaTicketAlt,
  FaUsers,
  FaHandshake,
  FaTrash,
  FaEdit,
  FaFilter,
  FaChevronDown,
  FaMoneyBill,
  FaPercent,
  FaTag,
  FaClock,
  FaCheck,
  FaBan,
  FaCheckCircle,
  FaTools,
  FaSearch,
  FaPlus,
  FaGlobe, 
  FaSuitcase, 
  FaCalendarDay,
  FaCalendarAlt,
  FaCalendarWeek,
  FaPercentage,
  FaShoppingCart,
  FaEuroSign,
  FaMapMarkerAlt, 
  FaAnchor, 
  FaUserFriends, 
} from "react-icons/fa";
import { TbSailboat, TbBuildingEstate } from "react-icons/tb";

import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import YachtDetailsModal from "../components/YachtDetailsModal/YachtDetailsModal";
import FinanceManagement from "../components/FinanceManagement/FinanceManagement";
import PartnerManagement from "../components/PartnerManagement/PartnerManagement";
import CreateYachtModal from "../components/YachtDetailsModal/CreateYachtModal";
import CreateYachtBookingModal from "../components/YachtBookingModal/CreateYachtBookingModal";


const AdminDashboard = () => {
  const navigate = useNavigate();
    const MySwal = withReactContent(Swal);
  const formRef = useRef();

  const [isLoading, setIsLoading] = useState(true); // Renamed from isLoading
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(
    window.location.hostname === "transfer-athens.online"
      ? "transfers"
      : "yachts" // Default to yachts or transfers
  );

  // Data states
  const [yachtsData, setYachtsData] = useState([]);
  const [yachtBookingsData, setYachtBookingsData] = useState([]); // Renamed from yachtBookingsData for clarity if it holds general rentals
  const [couponsData, setCouponsData] = useState([]);
  const [partnersData, setPartners] = useState([]);
  const [transfersData, setTransfersData] = useState([]);
  const [driversData, setDriversData] = useState([]);
  const [toursData, setToursData] = useState([]);
  const [tourBookingsData, setTourBookingsData] = useState([]);

  // --- Yacht States ---
  const [yachtSearchQuery, setYachtSearchQuery] = useState("");
  const [selectedYachtType, setSelectedYachtType] = useState("all"); // Filter by yacht type
  const [yachtStatusFilter, setYachtStatusFilter] = useState("all"); // Filter by status (available, maintenance, etc.)
  const [yachtTypes, setYachtTypes] = useState([]); // Dynamic types from data
  const [createYachtModalOpen, setCreateYachtModalOpen] = useState(false);
  const [yachtDetailsModalOpen, setYachtDetailsModalOpen] = useState(false);
  const [selectedYacht, setSelectedYacht] = useState(null);
  const [isCreatingYacht, setIsCreatingYacht] = useState(false);
  const [updatingYachtId, setUpdatingYachtId] = useState(null);
  const [deletingYachtId, setDeletingYachtId] = useState(null);
  const [yachtPage, setYachtPage] = useState(1); // Pagination for yachts
  const YACHTS_PER_PAGE = 9;
  const BOOKINGS_PER_PAGE = 10;

  // Yacht Availability/Pricing States (Adapt based on Yacht model needs)
  const [yachtAvailability, setYachtAvailability] = useState({}); // Store availability overrides per yacht
  const [updatingAvailabilityId, setUpdatingAvailabilityId] = useState(null);

  // --- Yacht Booking States ---
  const [yachtBookingSearch, setYachtBookingSearch] = useState("");
  const [yachtBookingStatusFilter, setYachtBookingStatusFilter] =
    useState("all");
  const [yachtBookingDateFilter, setYachtBookingDateFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [updatingYachtBookingId, setUpdatingYachtBookingId] = useState(null); // For status updates
  const [updatingYachtBookingPaymentId, setUpdatingYachtBookingPaymentId] =
    useState(null); // For payment status
  const [
    updatingYachtBookingCommissionId,
    setUpdatingYachtBookingCommissionId,
  ] = useState(null); // For commission status
  const [deletingYachtBookingId, setDeletingYachtBookingId] = useState(null);
  const [yachtBookingPage, setYachtBookingPage] = useState(1); // Pagination for bookings
  const [isYachtBookingModalOpen, setIsYachtBookingModalOpen] = useState(false); // For creating bookings

  const [isRentalModalOpen, setIsRentalModalOpen] = useState(false); 

  // Coupon states
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deletingCouponId, setDeletingCouponId] = useState(null);
  const [durationDiscounts, setDurationDiscounts] = useState({
    shortTerm: { discount: 0 },
    mediumTerm: { discount: 0 },
    longTerm: { discount: 0 },
  });

  // Season definitions (Keep or adjust as needed)
  const seasons = {
    low: {
      ranges: [
        { start: "2025-04-01", end: "2025-06-25" },
        { start: "2025-09-16", end: "2025-10-30" },
      ],
      name: "Low Season",
      description: "April 1 - June 25, September 16 - October 30",
    },
    medium: {
      ranges: [
        { start: "2025-06-26", end: "2025-07-10" },
        { start: "2025-08-26", end: "2025-09-15" },
      ],
      name: "Medium Season",
      description: "June 26 - July 10, August 26 - September 15",
    },
    high: {
      ranges: [{ start: "2025-07-11", end: "2025-08-25" }],
      name: "High Season",
      description: "July 11 - August 25",
    },
  };  //   const season = seasons[seasonKey];

  //   try {
  //     setSettingSeasonalPrice({ carId, season: seasonKey });
  //     const result = await Swal.fire({
  //       title: `Set ${season.name} Prices`,
  //       html: `
  //         <div class="mb-4">
  //           <p class="text-sm text-gray-600">${season.description}</p>
  //         </div>
  //         <div class="form-group">
  //           <label class="block text-sm font-medium text-gray-700 mb-1">Price per day (€)</label>
  //           <input
  //             id="seasonPrice"
  //             type="number"
  //             class="w-full px-3 py-2 border rounded-md"
  //             value="${basePrice}"
  //             min="1"
  //             step="any"
  //           >
  //         </div>
  //       `,
  //       showCancelButton: true,
  //       confirmButtonText: "Set Prices",
  //       cancelButtonText: "Cancel",
  //       confirmButtonColor: "#1e3a8a", // blue-900
  //       preConfirm: () => {
  //         const price = document.getElementById("seasonPrice").value;
  //         if (!price || price <= 0) {
  //           Swal.showValidationMessage("Please enter a valid price");
  //           return false;
  //         }
  //         return price;
  //       },
  //     });

  //     if (result.isConfirmed) {
  //       const price = parseFloat(result.value);

  //       // Show loading state with progress
  //       const loadingAlert = Swal.fire({
  //         title: "Setting Seasonal Prices",
  //         html: "Processing dates...",
  //         allowOutsideClick: false,
  //         didOpen: () => {
  //           Swal.showLoading();
  //         },
  //       });

  //       // Process each date range for the season
  //       for (const range of season.ranges) {
  //         const dates = [];
  //         let currentDate = moment
  //           .tz(range.start, "Europe/Athens")
  //           .startOf("day");
  //         const endDate = moment.tz(range.end, "Europe/Athens").startOf("day");

  //         while (currentDate.isSameOrBefore(endDate)) {
  //           dates.push({
  //             date: currentDate.toDate(),
  //             isAvailable: true,
  //             dynamicPrice: price,
  //           });
  //           currentDate = currentDate.clone().add(1, "day");
  //         }

  //         // Update all dates in the range
  //         await Promise.all(
  //           dates.map((dateData) => cars.updateAvailability(carId, dateData))
  //         );
  //       }

  //       await loadingAlert.close();
  //       await Swal.fire({
  //         icon: "success",
  //         title: "Prices Updated",
  //         text: `${season.name} prices have been set to €${price} per day`,
  //         confirmButtonColor: "#1e3a8a",
  //       });

  //       fetchData(); // Refresh data
  //     }
  //   } catch (error) {
  //     console.error("Error setting seasonal prices:", error);
  //     await Swal.fire({
  //       icon: "error",
  //       title: "Error",
  //       text: "Failed to update seasonal prices. Please try again.",
  //       confirmButtonColor: "#1e3a8a",
  //     });
  //   } finally {
  //     setSettingSeasonalPrice(null);
  //   }
  // };

  const handleYachtDelete = async (yachtId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this! Associated bookings might be affected.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        setDeletingYachtId(yachtId);
        await yachts.delete(yachtId);
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The yacht has been deleted.",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData(); // Refresh data
        // Close details modal if the deleted yacht was selected
        if (selectedYacht?._id === yachtId) {
          setYachtDetailsModalOpen(false);
          setSelectedYacht(null);
        }
      }
    } catch (error) {
      console.error("Error deleting yacht:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to delete yacht: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingYachtId(null);
    }
  };

  // set partners from partners.getAll();
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await partners.getAll();
        console.log("Partners data:", response.data);
        setPartners(response.data);
      } catch (error) {
        console.error("Error fetching partners:", error);
        setError("Failed to load partners data");
      }
    };
    fetchPartners();
  }, []);

  useEffect(() => {
    const verifyAuthAndFetch = async () => {
      try {
        const token =
          localStorage.getItem("adminToken") ||
          localStorage.getItem("Authentication:");
        if (!token) {
          throw new Error("No token found");
        }
        await auth.getCurrentUser(); // Verify token is valid
        fetchData(); // Fetch all necessary data
        // fetchDurationDiscounts(); // Fetch discounts if needed
      } catch (error) {
        console.error("Authentication failed or initial fetch error:", error);
        navigate("/admin/login"); // Redirect to login if auth fails
      }
    };

    verifyAuthAndFetch();
  }, [navigate]);

  const handleDurationDiscountUpdate = async (term, value) => {
    try {
      setDurationDiscounts((prev) => ({
        ...prev,
        [term]: { ...prev[term], discount: parseInt(value) },
      }));

      await rentals.updateDurationDiscount(term, parseInt(value));
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Duration discount updated successfully!",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating duration discount:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update duration discount. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
      // Revert the state on error
      // fetchDurationDiscounts();
    }
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const token =
          localStorage.getItem("adminToken") ||
          localStorage.getItem("Authentication:");
        if (!token) {
          throw new Error("No token found");
        }
        await auth.getCurrentUser();
        fetchData();
        // fetchDurationDiscounts();
      } catch (error) {
        console.error("Authentication failed:", error);
        navigate("/admin/login");
      }
    };

    verifyAuth();
  }, [navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        yachtsRes, // Fetch yachts
        yachtBookingsRes, // Fetch yacht bookings
        couponsRes,
        transfersRes,
        driversRes,
        toursRes,
        tourBookingsRes,
        partnersRes, // Fetch partners here too
      ] = await Promise.all([
        yachts.getAll(),
        yachtBookings.getAll(),
        coupons.getAll(),
        contact.getAllTransfers(),
        drivers.getAllDrivers(),
        tours.getAll(), // Consider tours.getAllAdmin() if needed
        tourBookings.getAll(),
        partners.getAll(), // Fetch partners
      ]);

      // Process Yacht Data
      setYachtsData(yachtsRes.data || []);
      // Extract unique yacht types for filter dropdown
      const uniqueYachtTypes = [
        ...new Set(yachtsRes.data.map((y) => y.yachtType).filter(Boolean)),
      ];
      setYachtTypes(uniqueYachtTypes);
      // Process yacht availability overrides
      const availabilityData = {};
      (yachtsRes.data || []).forEach((yacht) => {
        if (yacht.availability && yacht.availability.length > 0) {
          availabilityData[yacht._id] = yacht.availability;
        }
      });
      setYachtAvailability(availabilityData);

      // Process Yacht Booking Data
      setYachtBookingsData(yachtBookingsRes.data || []);

      // Process Other Data
      setCouponsData(couponsRes.data || []);
      setTransfersData(transfersRes.data || []);
      setDriversData(driversRes.data || []);
      setToursData(toursRes.data || []);
      setTourBookingsData(tourBookingsRes.data || []);
      setPartners(partnersRes.data || []); // Set partners data

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(`Failed to load dashboard data: ${error.message}`);
      setIsLoading(false);
    }
  };

  const handleCreateYachtBooking = async (bookingData) => {
    try {
      await yachtBookings.create(bookingData);
      await Swal.fire({
        icon: "success",
        title: "Booking Created",
        text: "Yacht booking created successfully.",
        confirmButtonColor: "#1e3a8a",
      });
      fetchData(); // Refresh bookings
      setIsYachtBookingModalOpen(false); // Close modal
    } catch (error) {
      console.error("Error creating yacht booking:", error);
      await Swal.fire({
        icon: "error",
        title: "Booking Error",
        text: `Failed to create yacht booking: ${error.message}`,
        confirmButtonColor: "#1e3a8a",
      });
    }
  };

  const handleYachtBookingStatusUpdate = async (bookingId, status) => {
    setUpdatingYachtBookingId(bookingId);
    try {
      await yachtBookings.updateStatus(bookingId, status);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating yacht booking status:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to update booking status: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingYachtBookingId(null);
    }
  };

  const handleYachtBookingPaymentUpdate = async (bookingId, isPaid) => {
    setUpdatingYachtBookingPaymentId(bookingId);
    try {
      await yachtBookings.updatePaymentStatus(bookingId, isPaid);
      // Optional: Show success message
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating yacht booking payment status:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to update payment status: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingYachtBookingPaymentId(null);
    }
  };

  const handleYachtBookingCommissionUpdate = async (
    bookingId,
    commissionPaid
  ) => {
    setUpdatingYachtBookingCommissionId(bookingId);
    try {
      await yachtBookings.updateCommissionPayment(bookingId, commissionPaid);
      // Optional: Show success message
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating yacht booking commission status:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to update commission status: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingYachtBookingCommissionId(null);
    }
  };

  const handleYachtBookingDelete = async (bookingId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to recover this booking record!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        setDeletingYachtBookingId(bookingId);
        await yachtBookings.delete(bookingId);
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The yacht booking record has been deleted.",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error("Error deleting yacht booking:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to delete booking: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingYachtBookingId(null);
    }
  };

  // COUPONS
  const handleCouponCreate = async (couponData) => {
    try {
      setCreatingCoupon(true);
      await coupons.create(couponData);
      fetchData();
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Coupon created successfully!",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error creating coupon:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create coupon. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setCreatingCoupon(false);
    }
  };

  const handleCouponDelete = async (couponId) => {
    try {
      setDeletingCouponId(couponId);
      await coupons.delete(couponId);
      fetchData();
    } catch (error) {
      console.error("Error deleting coupon:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete coupon. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingCouponId(null);
    }
  };

  const handleCouponEdit = async (couponData) => {
    try {
      await coupons.update(editingCoupon._id, couponData);
      setEditingCoupon(null);
      fetchData();
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Coupon updated successfully!",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating coupon:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update coupon. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    }
  };

  const handleCreateYacht = async (formData) => {
    // Assumes formData is already a FormData object from CreateYachtModal
    setIsCreatingYacht(true);
    try {
      await yachts.create(formData);
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Yacht created successfully!",
        confirmButtonColor: "#1e3a8a",
      });
      fetchData(); // Refresh data
      setCreateYachtModalOpen(false); // Close modal
    } catch (error) {
      console.error("Error creating yacht:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to create yacht: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setIsCreatingYacht(false);
    }
  };

  const handleUpdateYacht = async (yachtId, formData) => {
    setUpdatingYachtId(yachtId);
    try {
      await yachts.update(yachtId, formData);
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Yacht updated successfully!",
        confirmButtonColor: "#1e3a8a",
      });
      fetchData(); // Refresh data
      setYachtDetailsModalOpen(false); // Close modal
      setSelectedYacht(null);
    } catch (error) {
      console.error("Error updating yacht:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to update yacht: ${error.message}. Please try again.`,
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingYachtId(null);
    }
  };

  const formatPrice = (amount) => {
    return `€${amount.toFixed(2)}`;
  };

  const filteredYachts = yachtsData.filter((yacht) => {
    const typeMatch =
      selectedYachtType === "all" || yacht.yachtType === selectedYachtType;
    const statusMatch =
      yachtStatusFilter === "all" || yacht.status === yachtStatusFilter;
    const searchMatch =
      yachtSearchQuery === "" ||
      yacht.name.toLowerCase().includes(yachtSearchQuery.toLowerCase()) ||
      yacht.builder.toLowerCase().includes(yachtSearchQuery.toLowerCase());
    return typeMatch && statusMatch && searchMatch;
  });

  // Filter Yacht Bookings
  const filteredYachtBookings = yachtBookingsData.filter((booking) => {
    const matchesSearch =
      !yachtBookingSearch ||
      booking.customer.firstName
        ?.toLowerCase()
        .includes(yachtBookingSearch.toLowerCase()) ||
      booking.customer.lastName
        ?.toLowerCase()
        .includes(yachtBookingSearch.toLowerCase()) ||
      booking.yachtName
        ?.toLowerCase()
        .includes(yachtBookingSearch.toLowerCase()) ||
      booking.customer.email
        ?.toLowerCase()
        .includes(yachtBookingSearch.toLowerCase()) ||
      booking._id?.toLowerCase().includes(yachtBookingSearch.toLowerCase()); // Search by booking ID too

    const matchesStatus =
      yachtBookingStatusFilter === "all" ||
      booking.status === yachtBookingStatusFilter;

    const matchesDate =
      !yachtBookingDateFilter.startDate ||
      !yachtBookingDateFilter.endDate ||
      (moment(booking.bookingDate).isSameOrAfter(
        yachtBookingDateFilter.startDate,
        "day"
      ) &&
        moment(booking.bookingDate).isSameOrBefore(
          yachtBookingDateFilter.endDate,
          "day"
        ));

    return matchesSearch && matchesStatus && matchesDate;
  });

  // --- Pagination Logic ---

  // Yacht Pagination
  const totalYachtPages = Math.ceil(filteredYachts.length / YACHTS_PER_PAGE);
  const paginatedYachts = filteredYachts.slice(
    (yachtPage - 1) * YACHTS_PER_PAGE,
    yachtPage * YACHTS_PER_PAGE
  );
  // Reset yacht page if filters change and current page becomes invalid
  useEffect(() => {
    if (yachtPage > totalYachtPages && totalYachtPages > 0) {
      setYachtPage(1);
    }
  }, [
    yachtSearchQuery,
    selectedYachtType,
    yachtStatusFilter,
    yachtsData,
    yachtPage,
    totalYachtPages,
  ]);

  // Yacht Booking Pagination
  const totalBookingPages = Math.ceil(
    filteredYachtBookings.length / BOOKINGS_PER_PAGE
  );
  const paginatedYachtBookings = filteredYachtBookings.slice(
    (yachtBookingPage - 1) * BOOKINGS_PER_PAGE,
    yachtBookingPage * BOOKINGS_PER_PAGE
  );
  // Reset booking page if filters change
  useEffect(() => {
    if (yachtBookingPage > totalBookingPages && totalBookingPages > 0) {
      setYachtBookingPage(1);
    }
  }, [
    yachtBookingSearch,
    yachtBookingStatusFilter,
    yachtBookingDateFilter,
    yachtBookingsData,
    yachtBookingPage,
    totalBookingPages,
  ]);

     const handleCreateTour = () => {
    MySwal.fire({
      title: "Create New Tour",
      html: <CreateTourForm ref={formRef} />,
      showCancelButton: true,
      confirmButtonText: "Create Tour",
      confirmButtonColor: "#1e3a8a",
      cancelButtonColor: "#d33",
      width: '800px', 
      preConfirm: () => {
        const tourData = formRef.current?.getFormData();

        if (!tourData) {
           Swal.showValidationMessage(
            `Please fill all required fields correctly.` 
           );
           return false; 
         }
        return tourData;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const tourData = result.value;

        // Create FormData object
        const formData = new FormData();

        // Append all tour data
        formData.append("name", tourData.name);
        formData.append("description", tourData.description);
        formData.append("duration", tourData.duration);
        formData.append("maxPersons", tourData.maxPersons);
        formData.append("vehicleType", tourData.vehicleType);
        formData.append("isActive", tourData.isActive);

        // Add the image file if it exists
        if (tourData.tourImage) {
          formData.append("tourImage", tourData.tourImage);
        }

        for (const vehicle in tourData.pricing) {
           for (const season in tourData.pricing[vehicle]) {
               formData.append(`pricing[${vehicle}][${season}]`, tourData.pricing[vehicle][season]);
           }
        }

        // Append locations and highlights arrays
        tourData.locations.forEach(loc => formData.append('locations[]', loc));
        tourData.highlights.forEach(h => formData.append('highlights[]', h));

        tours
          .create(formData)
          .then(() => {
            MySwal.fire({
              icon: "success",
              title: "Tour Created!",
              text: "The tour has been created successfully.",
              confirmButtonColor: "#1e3a8a",
              iconColor: "#10B981", // Use MySwal's iconColor prop
            });
            fetchData(); // Refresh tour data
          })
          .catch((error) => {
            console.error("Error creating tour:", error);
            // Attempt to get specific error message from backend response if available
            const errorMsg = error.response?.data?.message || error.message || "An unknown error occurred.";
            MySwal.fire({ // Use MySwal
              icon: "error",
              title: "Creation Failed",
              text: `Failed to create tour: ${errorMsg}`,
              confirmButtonColor: "#1e3a8a",
            });
          });
      }
    });
  };

  const handleEditTour = (tourId) => {
    const tour = toursData.find((t) => t._id === tourId); 
    if (!tour) {
      console.error("Tour not found:", tourId);
      return;
    }
    MySwal.fire({
      title: "Edit Tour",
      html: <EditTourForm ref={formRef} tour={tour} />,
      showCancelButton: true,
      confirmButtonText: "Update Tour",
      confirmButtonColor: "#1e3a8a",
      cancelButtonColor: "#d33",
      width: '800px',
      preConfirm: () => {
        const tourData = formRef.current?.getFormData();
        if (!tourData) {
          Swal.showValidationMessage(
            `Please fill all required fields correctly.`
          );
          return false;
        }
        return tourData;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const tourData = result.value;
        const formData = new FormData();
        formData.append("name", tourData.name);
        formData.append("description", tourData.description);
        formData.append("duration", tourData.duration);
        formData.append("maxPersons", tourData.maxPersons);
        formData.append("vehicleType", tourData.vehicleType);
        formData.append("isActive", tourData.isActive);
        if (tourData.tourImage) {
          formData.append("tourImage", tourData.tourImage);
        }
        for (const vehicle in tourData.pricing) {
          for (const season in tourData.pricing[vehicle]) {
            formData.append(
              `pricing[${vehicle}][${season}]`,
              tourData.pricing[vehicle][season]
            );
          }
        }

        tourData.locations.forEach((loc) =>
          formData.append("locations[]", loc)
        );
        tourData.highlights.forEach((h) =>
          formData.append("highlights[]", h)
        );
        tours
          .update(tourId, formData)

          .then(() => {
            MySwal.fire({
              icon: "success",
              title: "Tour Updated!",
              text: "The tour has been updated successfully.",
              confirmButtonColor: "#1e3a8a",
              iconColor: "#10B981",
            });
            fetchData(); // Refresh tour data

          })
          .catch((error) => {
            console.error("Error updating tour:", error);
            const errorMsg =
              error.response?.data?.message || error.message || "An unknown error occurred.";
            MySwal.fire({
              icon: "error",
              title: "Update Failed",
              text: `Failed to update tour: ${errorMsg}`,
              confirmButtonColor: "#1e3a8a",
            });
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { id: "yachts", name: "Yachts", icon: FaShip },
    { id: "yachtBookings", name: "Yacht Bookings", icon: FaTicketAlt },
    { id: "tours", name: "Tours", icon: FaGlobe },
    { id: "finances", name: "Finances", icon: FaMoneyBill },
    { id: "coupons", name: "Coupons", icon: FaUsers },
    { id: "partners", name: "Partners", icon: FaHandshake },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-t from-blue-500 via-sky-100 to-sky-300 JosefinSans">
      <Header />
      <div className="flex-grow container mx-auto px-4 py-12">
        <div className=" rounded-lg shadow-md p-6 bg-gradient-to-t from-sky-200 via-sky-300 to-blue-400 border-2 border-red-100">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Admin Dashboard
            </h1>
            <NotificationSystem
              rentals={yachtBookingsData}
              onRentalUpdate={handleYachtBookingStatusUpdate}
            />
          </div>

          {/* Tabs */}
          <div className="overflow-x-auto pb-1 -mb-px">
            <div className="flex space-x-1 sm:space-x-4 border-b border-gray-200 mb-6 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center whitespace-nowrap px-3 py-2 border-b-2 font-medium text-xs sm:text-sm ${
                    activeTab === tab.id
                      ? "border-blue-900 text-blue-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden xs:inline">{tab.name}</span>
                  <span className="xs:hidden">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* --- Yachts Tab Content --- */}
          {activeTab === "yachts" && (
            <AnimatePresence mode="wait">
              <motion.div
                key="yachts-content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {/* Yachts Statistics Dashboard */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Total Yachts */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-blue-500 to-blue-700 p-5 rounded-xl shadow-lg text-white"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-blue-400 bg-opacity-50">
                        <FaShip className="w-6 h-6" />
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90">Total Yachts</p>
                        <p className="text-2xl font-bold">
                          {yachtsData.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                  {/* Available Yachts */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-br from-green-500 to-green-700 p-5 rounded-xl shadow-lg text-white"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-green-400 bg-opacity-50">
                        <FaCheckCircle className="w-6 h-6" />
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90">Available</p>
                        <p className="text-2xl font-bold">
                          {
                            yachtsData.filter((y) => y.status === "available")
                              .length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>
                  {/* Yachts In Maintenance */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-5 rounded-xl shadow-lg text-white"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-yellow-400 bg-opacity-50">
                        <FaTools className="w-6 h-6" />
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90">In Maintenance</p>
                        <p className="text-2xl font-bold">
                          {
                            yachtsData.filter((y) => y.status === "maintenance")
                              .length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>
                  {/* Unavailable Yachts */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gradient-to-br from-red-500 to-red-700 p-5 rounded-xl shadow-lg text-white"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-red-400 bg-opacity-50">
                        <FaBan className="w-6 h-6" />
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90">Unavailable</p>
                        <p className="text-2xl font-bold">
                          {
                            yachtsData.filter((y) => y.status === "unavailable")
                              .length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Search and Filter Section */}
                <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Search Input */}
                    <div className="flex-grow relative">
                      <input
                        type="text"
                        placeholder="Search yachts by name or builder..."
                        value={yachtSearchQuery}
                        onChange={(e) => setYachtSearchQuery(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 pl-11 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300"
                      />
                      <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>

                    {/* Filter Dropdowns */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-gray-200">
                        <FaAnchor className="text-gray-500 h-4 w-4" />{" "}
                        {/* Yacht Type Icon */}
                        <select
                          value={selectedYachtType}
                          onChange={(e) => setSelectedYachtType(e.target.value)}
                          className="border-none focus:ring-0 text-sm text-gray-600 bg-transparent flex-grow"
                          aria-label="Filter by Yacht Type"
                        >
                          <option value="all">All Types</option>
                          {yachtTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-gray-200">
                        <FaFilter className="text-gray-500 h-4 w-4" />{" "}
                        {/* Status Icon */}
                        <select
                          value={yachtStatusFilter}
                          onChange={(e) => setYachtStatusFilter(e.target.value)}
                          className="border-none focus:ring-0 text-sm text-gray-600 bg-transparent flex-grow"
                          aria-label="Filter by Status"
                        >
                          <option value="all">All Statuses</option>
                          <option value="available">Available</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="unavailable">Unavailable</option>
                        </select>
                      </div>
                    </div>

                    {/* Add Yacht Button */}
                    <motion.button
                      onClick={() => setCreateYachtModalOpen(true)}
                      disabled={isCreatingYacht}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 shadow-md flex items-center justify-center text-sm font-medium"
                    >
                      {isCreatingYacht ? (
                        <>
                          <LoadingSpinner size="sm" color="text-white" />
                          <span className="ml-2">Creating...</span>
                        </>
                      ) : (
                        <>
                          <FaPlus className="mr-2 h-4 w-4" />
                          <span>Add New Yacht</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>

                {/* Yacht Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedYachts.map((yacht) => (
                    <motion.div
                      key={yacht._id}
                      layout // Animate layout changes
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }} // Added exit animation
                      whileHover={{
                        y: -5,
                        boxShadow:
                          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                      }} // Enhanced hover effect
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 flex flex-col"
                    >
                      {/* Yacht Image */}
                                  <div className="relative aspect-w-16 aspect-h-9 bg-gray-100">
                                  <img
                                    src={yacht.images?.[0]?.startsWith('/uploads') ? 
                                      `https://yacht-server.create-site.gr${yacht.images[0]}` : 
                                      yacht.images?.[0]}
                          alt={yacht.name}
                          className="object-cover w-full h-48" // Fixed height for consistency
                        />
                        {/* Status Badge */}
                        <div className="absolute top-3 right-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                              yacht.status === "available"
                                ? "bg-green-100 text-green-800"
                                : yacht.status === "maintenance"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {yacht.status || "Unknown"}
                          </span>
                        </div>
                      </div>

                      {/* Yacht Info */}
                      <div className="p-5 flex-grow flex flex-col justify-between">
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <h3
                              className="text-lg font-bold text-gray-800 truncate"
                              title={yacht.name}
                            >
                              {yacht.name}
                            </h3>
                            {/* Edit Button */}
                            <button
                              onClick={
                                () => {
                                  console.log("Edit button clicked for yacht:", yacht);
                                  setSelectedYacht(yacht);
                                  setYachtDetailsModalOpen(true);
                                }
                              }
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                              aria-label={`Edit ${yacht.name}`}
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-500 mb-3">
                            {yacht.builder} • {yacht.yachtType}
                          </p>

                          {/* Key Specs */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600 mb-4">
                            <div className="flex items-center">
                              <FaUserFriends className="w-4 h-4 mr-2 text-blue-500" />
                              <span>{yacht.guestCapacity} Guests</span>
                            </div>
                            <div className="flex items-center">
                              <TbSailboat className="w-4 h-4 mr-2 text-indigo-500" />{" "}
                              {/* Length Icon */}
                              <span>
                                {yacht.specifications?.length || "N/A"}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <FaAnchor className="w-4 h-4 mr-2 text-teal-500" />{" "}
                              {/* Type Icon */}
                              <span>{yacht.yachtType}</span>
                            </div>
                            <div className="flex items-center">
                              <TbBuildingEstate className="w-4 h-4 mr-2 text-purple-500" />{" "}
                              {/* Builder Icon */}
                              <span>{yacht.builder}</span>
                            </div>
                          </div>
                        </div>

                        {/* Price/Units Info & Actions */}
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex justify-between items-center">
                            <div>
                              {/* Pricing for yachts is complex, maybe show starting price or remove */}
                              {/* <span className="text-xs text-gray-500">Starting Price</span>
                                  <p className="text-md font-bold text-blue-600">€{yacht.pricing?....}/day</p> */}
                              <span className="text-xs text-gray-500">
                                Units
                              </span>
                              <p className="text-md font-bold text-gray-800">
                                {yacht.totalUnits || 1}
                              </p>
                            </div>
                            {/* Delete Button */}
                            <button
                              onClick={() => handleYachtDelete(yacht._id)}
                              disabled={deletingYachtId === yacht._id}
                              className="p-2 text-red-500 hover:bg-blue-50 rounded-md transition-colors duration-200 disabled:opacity-50"
                              aria-label={`Delete ${yacht.name}`}
                            >
                              {deletingYachtId === yacht._id ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <FaTrash className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  {/* No Yachts Found Message */}
                  {paginatedYachts.length === 0 && (
                    <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center py-10 text-gray-500">
                      No yachts found matching your criteria.
                    </div>
                  )}
                </div>

                {/* Yacht Pagination Controls */}
                {totalYachtPages > 1 && (
                  <div className="flex justify-center items-center mt-8 gap-3">
                    <button
                      onClick={() => setYachtPage(Math.max(yachtPage - 1, 1))}
                      disabled={yachtPage === 1}
                      className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous Yacht Page"
                    >
                      Previous
                    </button>
                    <span className="text-gray-700 text-sm">
                      Page {yachtPage} of {totalYachtPages}
                    </span>
                    <button
                      onClick={() =>
                        setYachtPage(Math.min(yachtPage + 1, totalYachtPages))
                      }
                      disabled={yachtPage === totalYachtPages}
                      className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next Yacht Page"
                    >
                      Next
                    </button>
                  </div>
                )}

                <CreateYachtModal
                  isOpen={createYachtModalOpen}
                  onClose={() => setCreateYachtModalOpen(false)}
                  onSave={handleCreateYacht}
                  isCreating={isCreatingYacht}
                />

                  <YachtDetailsModal
                    yacht={selectedYacht} // Pass initial data
                    isOpen={yachtDetailsModalOpen}
                    onClose={() => {
                      setYachtDetailsModalOpen(false);
                      setSelectedYacht(null);
                    }}
                    onSave={(id, data) => handleUpdateYacht(id, data)}
                    onDelete={handleYachtDelete}
                    isUpdating={updatingYachtId === selectedYacht?._id}
                    isDeleting={deletingYachtId === selectedYacht?._id}
                    // Pass availability data if needed
                    yachtAvailability={
                      yachtAvailability[selectedYacht?._id] || []
                    }
                  />
                
              </motion.div>
            </AnimatePresence>
          )}

          {/* --- Yacht Bookings Tab Content --- */}
          {activeTab === "yachtBookings" && (
            <AnimatePresence mode="wait">
              <motion.div
                key="yacht-bookings-content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {/* Search and Filter Section */}
                <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                    {/* Search Input */}
                    <div className="relative md:col-span-2">
                      <label
                        htmlFor="bookingSearch"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Search Bookings
                      </label>
                      <input
                        id="bookingSearch"
                        type="text"
                        placeholder="Search by name, email, yacht, ID..."
                        value={yachtBookingSearch}
                        onChange={(e) => setYachtBookingSearch(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 mt-2.5 text-gray-400" />
                    </div>

                    {/* Status Filter */}
                    <div>
                      <label
                        htmlFor="bookingStatusFilter"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Status
                      </label>
                      <select
                        id="bookingStatusFilter"
                        value={yachtBookingStatusFilter}
                        onChange={(e) =>
                          setYachtBookingStatusFilter(e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {/* Date Filters */}
                    <div className="grid grid-cols-2 gap-2 md:col-span-2">
                      <div>
                        <label
                          htmlFor="bookingStartDate"
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Start Date
                        </label>
                        <DatePicker
                          id="bookingStartDate"
                          selected={yachtBookingDateFilter.startDate}
                          onChange={(date) =>
                            setYachtBookingDateFilter((prev) => ({
                              ...prev,
                              startDate: date,
                            }))
                          }
                          placeholderText="Start Date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          dateFormat="dd/MM/yyyy"
                          selectsStart
                          startDate={yachtBookingDateFilter.startDate}
                          endDate={yachtBookingDateFilter.endDate}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="bookingEndDate"
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          End Date
                        </label>
                        <DatePicker
                          id="bookingEndDate"
                          selected={yachtBookingDateFilter.endDate}
                          onChange={(date) =>
                            setYachtBookingDateFilter((prev) => ({
                              ...prev,
                              endDate: date,
                            }))
                          }
                          placeholderText="End Date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          dateFormat="dd/MM/yyyy"
                          selectsEnd
                          startDate={yachtBookingDateFilter.startDate}
                          endDate={yachtBookingDateFilter.endDate}
                          minDate={yachtBookingDateFilter.startDate}
                        />
                      </div>
                    </div>
                    {/* Optional: Add Create Booking Button */}
                    {/* <motion.button
                        onClick={() => setIsYachtBookingModalOpen(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 flex items-center justify-center space-x-2 text-sm"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <FaPlus className="w-4 h-4" />
                        <span>Create Booking</span>
                    </motion.button> */}
                  </div>
                </div>

{/* Yacht Bookings Table */}
<div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gradient-to-r from-blue-600 to-teal-500 text-white">
      <tr>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Customer Details
        </th>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Booking Info
        </th>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Destination & Notes
        </th>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Price & Payment
        </th>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Commission Details
        </th>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Status
        </th>
        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
          Actions
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {paginatedYachtBookings.map((booking) => {
        const partner = partnersData.find(
          (p) => p._id === booking.professionalPartner
        );
        return (
          <motion.tr
            key={booking._id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="hover:bg-blue-50 transition-all duration-300"
          >
            {/* Customer Info */}
            <td className="px-4 py-4">
              <div className="text-sm font-medium text-gray-900">
                {booking.customer.firstName} {booking.customer.lastName}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                <span className="inline-block mr-1">📧</span> {booking.customer.email}
              </div>
              <div className="text-xs text-gray-500">
                <span className="inline-block mr-1">📱</span> {booking.customer.phoneNumber}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Created: {new Date(booking.createdAt).toLocaleDateString()}
              </div>
            </td>
            
            {/* Booking Info */}
            <td className="px-4 py-4">
              <div className="text-sm font-medium text-gray-900">
                {booking.yachtName}
              </div>
              <div className="text-xs text-blue-600 font-medium mt-1">
                {booking.cruiseType}
              </div>
              <div className="text-xs text-gray-600 mt-2">
                <span className="inline-block mr-1">📅</span> {new Date(booking.bookingDate).toLocaleDateString()}
              </div>
              <div className="text-xs text-gray-600">
                <span className="inline-block mr-1">⏰</span> {booking.bookingTime}
              </div>
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mt-1 inline-block">
                {booking.numberOfGuests} Guests
              </div>
            </td>
            
            {/* Destination & Notes */}
            <td className="px-4 py-4">
              <div className="flex flex-wrap gap-1 mb-2">
                {booking.destination.map((dest, index) => (
                  <span 
                    key={index} 
                    className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded-full capitalize"
                  >
                    {dest}
                  </span>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-700 max-w-xs">
                <span className="font-medium">Notes:</span> 
                <div className="mt-1 bg-gray-50 p-2 rounded">
                  {booking.additionalNotes || "No additional notes"}
                </div>
              </div>
            </td>
            
            {/* Price & Payment */}
            <td className="px-4 py-4">
              <div className="text-sm font-semibold text-blue-600">
                {formatPrice(booking.calculatedPrice)}
              </div>
              {booking.couponDiscountAmount > 0 && (
                <div className="text-xs text-green-600 mt-1">
                  Discount: {formatPrice(booking.couponDiscountAmount)}
                </div>
              )}
              <button
                onClick={() => handleYachtBookingPaymentUpdate(booking._id, !booking.isPaid)}
                disabled={updatingYachtBookingPaymentId === booking._id}
                className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors duration-200 ${
                  booking.isPaid
                    ? "bg-green-100 text-green-800 hover:bg-green-200"
                    : "bg-red-100 text-red-800 hover:bg-red-200"
                }`}
              >
                {updatingYachtBookingPaymentId === booking._id ? (
                  <LoadingSpinner size="xs" />
                ) : booking.isPaid ? (
                  "✓ Paid"
                ) : (
                  "✕ Unpaid"
                )}
              </button>
            </td>
            
            <td className="px-4 py-4">
              {booking.professionalPartner ? (
                <>
                  <div className="text-sm font-medium text-gray-900">
                    {booking.professionalPartner.firstName} {booking.professionalPartner.lastName}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Comm: {formatPrice(booking.commissionAmount)} ({booking.professionalPartner.commissionRate}%)
                  </div>
                  <button
                    onClick={() => handleYachtBookingCommissionUpdate(booking._id, !booking.commissionPaid)}
                    disabled={updatingYachtBookingCommissionId === booking._id}
                    className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors duration-200 ${
                      booking.commissionPaid
                        ? "bg-green-100 text-green-800 hover:bg-green-200"
                        : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    }`}
                  >
                    {updatingYachtBookingCommissionId === booking._id ? (
                      <LoadingSpinner size="xs" />
                    ) : booking.commissionPaid ? (
                      "✓ Comm. Paid"
                    ) : (
                      "○ Comm. Unpaid"
                    )}
                  </button>
                </>
              ) : (
                <span className="text-xs text-gray-400 italic">
                  No Partner Assigned
                </span>
              )}
            </td>

            {/* Status */}
            <td className="px-4 py-4">
              <select
                value={booking.status}
                onChange={(e) => handleYachtBookingStatusUpdate(booking._id, e.target.value)}
                disabled={updatingYachtBookingId === booking._id}
                className={`text-xs p-2 rounded-md border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  booking.status === "completed"
                    ? "bg-green-50 border-green-200 text-green-700"
                    : booking.status === "cancelled"
                    ? "bg-red-50 border-red-200 text-red-700"
                    : booking.status === "confirmed"
                    ? "bg-blue-50 border-blue-200 text-blue-700"
                    : "bg-yellow-50 border-yellow-200 text-yellow-700"
                }`}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </td>
            
            {/* Actions */}
            <td className="px-4 py-4 text-right">
              <div className="flex items-center justify-end">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleYachtBookingDelete(booking._id)}
                  disabled={deletingYachtBookingId === booking._id}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors duration-200 disabled:opacity-50"
                  aria-label="Delete Booking"
                >
                  {deletingYachtBookingId === booking._id ? (
                    <LoadingSpinner size="xs" />
                  ) : (
                    <FaTrash className="w-4 h-4" />
                  )}
                </motion.button>
              </div>
            </td>
          </motion.tr>
        );
      })}
      
      {/* No Bookings Found Message */}
      {paginatedYachtBookings.length === 0 && (
        <motion.tr
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <td
            colSpan="7"
            className="px-6 py-10 text-center text-sm text-gray-500"
          >
            <div className="flex flex-col items-center justify-center">
              <span className="text-2xl mb-2">🔍</span>
              <span>No yacht bookings found matching your criteria.</span>
            </div>
          </td>
        </motion.tr>
      )}
    </tbody>
  </table>
</div>

                {/* Yacht Booking Pagination Controls */}
                {totalBookingPages > 1 && (
                  <div className="flex justify-center items-center mt-6 gap-3">
                    <button
                      onClick={() =>
                        setYachtBookingPage(Math.max(yachtBookingPage - 1, 1))
                      }
                      disabled={yachtBookingPage === 1}
                      className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous Booking Page"
                    >
                      Previous
                    </button>
                    <span className="text-gray-700 text-sm">
                      Page {yachtBookingPage} of {totalBookingPages}
                    </span>
                    <button
                      onClick={() =>
                        setYachtBookingPage(
                          Math.min(yachtBookingPage + 1, totalBookingPages)
                        )
                      }
                      disabled={yachtBookingPage === totalBookingPages}
                      className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next Booking Page"
                    >
                      Next
                    </button>
                  </div>
                )}
                <CreateYachtBookingModal
                isOpen={isYachtBookingModalOpen}
                onClose={() => setIsYachtBookingModalOpen(false)}
                onSave={handleCreateYachtBooking}
                yachts={yachtsData}
                partners={partnersData}
              />
              </motion.div>
            </AnimatePresence>
          )}

          {activeTab === "tours" && (
            <>
              <div className="bg-gray-100 p-6 rounded-lg shadow-md border border-gray-200 mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Tour Management
                  </h3>
 <motion.button
      onClick={handleCreateTour}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 shadow-lg flex items-center"
    >
      <FaPlus className="mr-2" />
      <span>Create New Tour</span>
    </motion.button>
                </div>

                {/* Tours Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-indigo-400 bg-opacity-40">
                        <FaSuitcase className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Total Tours
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {toursData.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                        <FaCheck className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">Active</p>
                        <p className="text-2xl font-bold text-white">
                          {toursData.filter((tour) => tour.isActive).length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
                        <FaUsers className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Booked Tours
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {tourBookingsData.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gradient-to-br from-amber-500 to-amber-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-amber-400 bg-opacity-40">
                        <FaClock className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Pending Tours
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {
                            tourBookingsData.filter(
                              (booking) => booking.status === "pending"
                            ).length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Tours List */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Available Tours
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {toursData.map((tour) => (
                      <motion.div
                        key={tour._id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.2 }}
                        className={`bg-white rounded-xl shadow-md border overflow-hidden transition-all duration-300 ${
                          !tour.isActive ? "opacity-60" : ""
                        }`}
                      >
                        <div className="relative h-48 bg-blue-50">
                          {tour.imageUrl ? (
                            <img
                              src={
                                tour.imageUrl.startsWith("http")
                                  ? tour.imageUrl
                                  : `${"https://yacht-server.create-site.gr"}${
                                      tour.imageUrl.startsWith("/") ? "" : "/"
                                    }${tour.imageUrl}`
                              }
                              alt={tour.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/images/placeholder-tour.jpg";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <FaSuitcase className="w-12 h-12 text-gray-300" />
                            </div>
                          )}
                          <div className="absolute top-4 right-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                tour.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {tour.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="flex justify-between items-start">
                            <h5 className="text-xl font-bold text-gray-900 mb-2">
                              {tour.name}
                            </h5>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleEditTour(tour._id)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  Swal.fire({
                                    title: "Delete Tour?",
                                    text: "This will permanently delete this tour. Are you sure?",
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonColor: "#d33",
                                    cancelButtonColor: "#3085d6",
                                    confirmButtonText: "Yes, delete it!",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      tours
                                        .delete(tour._id)
                                        .then(() => {
                                          Swal.fire(
                                            "Deleted!",
                                            "The tour has been deleted.",
                                            "success"
                                          );
                                          fetchData(); // Refresh tour data
                                        })
                                        .catch((error) => {
                                          console.error(
                                            "Error deleting tour:",
                                            error
                                          );
                                          Swal.fire({
                                            icon: "error",
                                            title: "Deletion Failed",
                                            text: "Failed to delete tour. Please try again.",
                                            confirmButtonColor: "#1e3a8a",
                                          });
                                        });
                                    }
                                  });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div className="mt-2">
                            <span className="text-sm text-gray-500">
                              {tour.duration} hours • Max {tour.maxPersons}{" "}
                              persons
                            </span>
                            <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                              <HTMLContent html={tour.description} />
                            </p>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">

                                <span
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  <FaMapMarkerAlt className="mr-1 w-3 h-3" />
                                  <HTMLContent html={tour.locations} />
                                </span>
                              
                            {tour.locations.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                +{tour.locations.length - 3} more
                              </span>
                            )}
                          </div>

                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-xs text-gray-500">
                                  Starting from
                                </p>
                                <div>
                                  {(() => {
                                    // Determine current season based on today's date
                                    const today = new Date();
                                    const month = today.getMonth() + 1; // JavaScript months are 0-based
                                    const day = today.getDate();

                                    // Default to low season if not matched
                                    let currentSeason = "low";

                                    // Check if current date falls within medium season
                                    if (
                                      (month === 6 && day >= 26) ||
                                      (month === 7 && day <= 10) ||
                                      (month === 8 && day >= 26) ||
                                      (month === 9 && day <= 15)
                                    ) {
                                      currentSeason = "medium";
                                    }
                                    // Check if current date falls within high season
                                    else if (
                                      (month === 7 && day >= 11) ||
                                      (month === 8 && day <= 25)
                                    ) {
                                      currentSeason = "high";
                                    }

                                    // Get price based on vehicle type and season
                                    const price =
                                      tour.vehicleType === "car"
                                        ? tour.pricing.car[
                                            `${currentSeason}Season`
                                          ]
                                        : tour.vehicleType === "minivan"
                                        ? tour.pricing.minivan[
                                            `${currentSeason}Season`
                                          ]
                                        : tour.vehicleType === "van"
                                        ? tour.pricing.van[
                                            `${currentSeason}Season`
                                          ]
                                        : tour.pricing.minibus[
                                            `${currentSeason}Season`
                                          ];

                                    return (
                                      <>
                                        <p className="text-lg font-bold text-blue-600">
                                          €{price}
                                        </p>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  tours
                                    .toggleActive(tour._id)
                                    .then(() => {
                                      Swal.fire({
                                        icon: "success",
                                        title: "Tour Status Updated!",
                                        text: `Tour has been ${
                                          tour.isActive
                                            ? "deactivated"
                                            : "activated"
                                        }.`,
                                        confirmButtonColor: "#1e3a8a",
                                      });
                                      fetchData(); // Refresh tour data
                                    })
                                    .catch((error) => {
                                      console.error(
                                        "Error toggling tour status:",
                                        error
                                      );
                                      Swal.fire({
                                        icon: "error",
                                        title: "Update Failed",
                                        text: "Failed to update tour status. Please try again.",
                                        confirmButtonColor: "#1e3a8a",
                                      });
                                    });
                                }}
                                className={`px-4 py-2 text-xs font-medium rounded-lg ${
                                  tour.isActive
                                    ? "bg-red-100 text-red-800 hover:bg-red-200"
                                    : "bg-green-100 text-green-800 hover:bg-green-200"
                                }`}
                              >
                                {tour.isActive ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Tour Bookings */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Tour Bookings
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tour
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date & Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Persons
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vehicle
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pickup Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Travel Guide
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {tourBookingsData.map((booking) => {
                          return (
                            <tr key={booking._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {booking.tour?.name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {moment(booking.tourDate).format("MMM D, YYYY")}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.numberOfPersons}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  €{booking.totalPrice}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 capitalize">
                                  {booking.vehicleType}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.pickupLocation}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 capitalize">
                                  {booking.paymentMethod?.replace("-", " ")}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.travelGuide.available ? 'Yes, for ' + booking.travelGuide.time : 'No'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  className="px-3 py-1 rounded bg-blue-100 text-blue-800 text-xs font-semibold hover:bg-blue-200 transition"
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Customer Information",
                                      html: `
                                        <div class="text-left space-y-2">
                                          <div>
                                            <span class="font-semibold">Name:</span>
                                            <span>${booking.name || ""}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Email:</span>
                                            <span>${booking.email || ""}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Phone:</span>
                                            <span>${booking.phone || ""}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Notes:</span>
                                            <span>${booking.notes || "-"}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Created:</span>
                                            <span>${moment(booking.createdAt).format("MMM D, YYYY HH:mm")}</span>
                                          </div>
                                        </div>
                                      `,
                                      confirmButtonColor: "#1e3a8a",
                                    });
                                  }}
                                >
                                  View
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                    ${
                                      booking.status === "pending"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : booking.status === "confirmed"
                                        ? "bg-blue-100 text-blue-800"
                                        : booking.status === "completed"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                >
                                  {booking.status.charAt(0).toUpperCase() +
                                    booking.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <select
                                    value={booking.status}
                                    onChange={(e) => {
                                      tourBookings
                                        .updateStatus(booking._id, e.target.value)
                                        .then(() => {
                                          Swal.fire({
                                            icon: "success",
                                            title: "Status Updated!",
                                            text: `Booking status changed.`,
                                            confirmButtonColor: "#1e3a8a",
                                            timer: 1500,
                                          });
                                          fetchData();
                                        })
                                        .catch((error) => {
                                          console.error(
                                            "Error updating booking status:",
                                            error
                                          );
                                          Swal.fire({
                                            icon: "error",
                                            title: "Update Failed",
                                            text: "Failed to update booking status.",
                                            confirmButtonColor: "#1e3a8a",
                                          });
                                        });
                                    }}
                                    className="text-sm border-gray-300 rounded-lg p-2"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                  </select>
                                  <button
                                    onClick={() => {
                                      Swal.fire({
                                        title: "Delete Booking?",
                                        text: "Are you sure you want to delete this tour booking?",
                                        icon: "warning",
                                        showCancelButton: true,
                                        confirmButtonColor: "#d33",
                                        cancelButtonColor: "#3085d6",
                                        confirmButtonText: "Yes, delete it!",
                                      }).then((result) => {
                                        if (result.isConfirmed) {
                                          tourBookings
                                            .delete(booking._id)
                                            .then(() => {
                                              Swal.fire(
                                                "Deleted!",
                                                "The booking has been deleted.",
                                                "success"
                                              );
                                              fetchData();
                                            })
                                            .catch((error) => {
                                              console.error(
                                                "Error deleting booking:",
                                                error
                                              );
                                              Swal.fire({
                                                icon: "error",
                                                title: "Deletion Failed",
                                                text: "Failed to delete booking.",
                                                confirmButtonColor: "#1e3a8a",
                                              });
                                            });
                                        }
                                      });
                                    }}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <FaTrash className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {tourBookingsData.length === 0 && (
                          <tr>
                            <td
                              colSpan="10"
                              className="px-6 py-4 text-center text-sm text-gray-500"
                            >
                              No tour bookings found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "finances" && <FinanceManagement />}

          {activeTab === "coupons" && (
            <div className="space-y-8 animate-fadeIn">
              {/* Coupon Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-sm border border-blue-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-500/20 text-blue-600">
                      <FaTag className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-blue-700">
                        Active Coupons
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 text-transparent bg-clip-text">
                        {
                          couponsData.filter(
                            (c) => new Date(c.expiryDate) > new Date()
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-sm border border-green-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-500/20 text-green-600">
                      <FaPercent className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-green-700">
                        Total Uses
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-800 text-transparent bg-clip-text">
                        {couponsData.reduce(
                          (total, c) => total + c.usageCount,
                          0
                        )}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-amber-500/20 text-amber-600">
                      <FaClock className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-amber-700">
                        Expiring Soon
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-800 text-transparent bg-clip-text">
                        {
                          couponsData.filter((c) => {
                            const daysUntilExpiry = Math.ceil(
                              (new Date(c.expiryDate) - new Date()) /
                                (1000 * 60 * 60 * 24)
                            );
                            return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
                          }).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl shadow-sm border border-red-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-red-500/20 text-red-600">
                      <FaBan className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-red-700">
                        Expired
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-800 text-transparent bg-clip-text">
                        {
                          couponsData.filter(
                            (c) => new Date(c.expiryDate) <= new Date()
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Coupon Creation/Edit Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
              >
                <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FaTicketAlt className="mr-2 text-indigo-600" />
                    {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingCoupon
                      ? "Modify existing coupon details"
                      : "Create a new discount coupon for your customers"}
                  </p>
                </div>
                <div className="p-6">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const couponData = {
                        code: formData.get("code").toUpperCase(),
                        discountType: formData.get("discountType"),
                        discountValue: Number(formData.get("discountValue")),
                        minOrderAmount: Number(formData.get("minOrderAmount")),
                        expiryDate: formData.get("expiryDate"),
                        maxUses: Number(formData.get("maxUses")),
                      };

                      if (editingCoupon) {
                        handleCouponEdit(couponData);
                      } else {
                        handleCouponCreate(couponData);
                      }
                    }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  >
                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaTag className="mr-2 text-blue-500" /> Coupon Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="code"
                          required
                          defaultValue={editingCoupon?.code || ""}
                          placeholder="e.g., SUMMER2025"
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all uppercase"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaPercentage className="mr-2 text-green-500" />{" "}
                        Discount Type
                      </label>
                      <div className="relative">
                        <select
                          name="discountType"
                          required
                          defaultValue={
                            editingCoupon?.discountType || "percentage"
                          }
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all appearance-none"
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (€)</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                          <FaChevronDown className="h-4 w-4" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaEuroSign className="mr-2 text-amber-500" /> Discount
                        Value
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          name="discountValue"
                          required
                          defaultValue={editingCoupon?.discountValue || ""}
                          placeholder="Enter amount or percentage"
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaShoppingCart className="mr-2 text-purple-500" />{" "}
                        Minimum Order Amount
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <span className="text-gray-500">€</span>
                        </div>
                        <input
                          type="number"
                          name="minOrderAmount"
                          required
                          defaultValue={editingCoupon?.minOrderAmount || ""}
                          placeholder="Minimum order value"
                          className="block w-full rounded-lg border-0 bg-gray-50 pl-8 pr-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaCalendarAlt className="mr-2 text-red-500" /> Expiry
                        Date
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          name="expiryDate"
                          required
                          defaultValue={
                            editingCoupon?.expiryDate
                              ? new Date(editingCoupon.expiryDate)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaUsers className="mr-2 text-blue-500" /> Maximum Uses
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          name="maxUses"
                          required
                          defaultValue={editingCoupon?.maxUses || ""}
                          placeholder="Maximum number of uses"
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2 lg:col-span-3 flex gap-4">
                      {editingCoupon && (
                        <button
                          type="button"
                          onClick={() => setEditingCoupon(null)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={creatingCoupon}
                        className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {creatingCoupon ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">
                              {editingCoupon ? "Updating..." : "Creating..."}
                            </span>
                          </>
                        ) : (
                          <span className="flex items-center">
                            {editingCoupon ? (
                              <>
                                <FaEdit className="mr-2" /> Update Coupon
                              </>
                            ) : (
                              <>
                                <FaPlus className="mr-2" /> Create Coupon
                              </>
                            )}
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>

              {/* Active Coupons Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <FaTicketAlt className="mr-2 text-indigo-600" />
                  Active Coupons
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence mode="wait">
                    {couponsData.map((coupon, index) => {
                      const isExpired =
                        new Date(coupon.expiryDate) <= new Date();
                      const usagePercentage =
                        (coupon.usageCount / coupon.maxUses) * 100;
                      const expiryDate = new Date(coupon.expiryDate);
                      const daysRemaining = Math.ceil(
                        (expiryDate - new Date()) / (1000 * 60 * 60 * 24)
                      );

                      // Determine status color
                      let statusColor = "green";
                      if (isExpired) {
                        statusColor = "red";
                      } else if (daysRemaining <= 3) {
                        statusColor = "red";
                      } else if (daysRemaining <= 7) {
                        statusColor = "amber";
                      }

                      return (
                        <motion.div
                          key={coupon._id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95, y: 20 }}
                          transition={{ delay: index * 0.05 }}
                          className={`bg-white rounded-xl shadow-sm border-2 ${
                            isExpired ? "border-red-300" : "border-gray-100"
                          } overflow-hidden hover:shadow-md transition-all duration-300 relative`}
                        >
                          <div
                            className={`h-2 w-full ${
                              isExpired
                                ? "bg-red-500"
                                : usagePercentage > 75
                                ? "bg-amber-500"
                                : "bg-green-500"
                            }`}
                          ></div>

                          <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-800">
                                    {coupon.code}
                                  </span>
                                  {isExpired && (
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 animate-pulse">
                                      EXPIRED
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  <p className="text-2xl font-bold text-gray-900 flex items-center">
                                    {coupon.discountType === "percentage" ? (
                                      <>
                                        <span className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-transparent bg-clip-text">
                                          {coupon.discountValue}%
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="bg-gradient-to-r from-blue-600 to-indigo-800 text-transparent bg-clip-text">
                                          €{coupon.discountValue}
                                        </span>
                                      </>
                                    )}
                                    <span className="text-sm font-normal text-gray-500 ml-1">
                                      discount
                                    </span>
                                  </p>
                                  <p className="text-sm text-gray-600 flex items-center">
                                    <FaShoppingCart className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                                    Minimum order:{" "}
                                    <span className="font-medium text-gray-800 ml-1">
                                      €{coupon.minOrderAmount}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingCoupon(coupon)}
                                  className="p-2 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-500 focus:outline-none transition-all"
                                >
                                  <FaEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleCouponDelete(coupon._id)}
                                  disabled={deletingCouponId === coupon._id}
                                  className="p-2 rounded-full hover:bg-blue-50 text-gray-400 hover:text-red-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                  {deletingCouponId === coupon._id ? (
                                    <LoadingSpinner size="sm" />
                                  ) : (
                                    <FaTrash className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </div>

                            <div className="px-6 pb-5 pt-3">
                              {/* Usage indicator with animation */}
                              <div className="mb-2 flex justify-between items-center text-xs text-gray-500">
                                <span>Usage</span>
                                <span className="font-medium">
                                  {coupon.usageCount} of {coupon.maxUses}
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-2 rounded-full ${
                                    isExpired
                                      ? "bg-gray-300"
                                      : usagePercentage > 75
                                      ? "bg-red-500"
                                      : usagePercentage > 50
                                      ? "bg-amber-500"
                                      : "bg-green-500"
                                  } transition-all duration-500 ease-out`}
                                  style={{
                                    width: `${usagePercentage}%`,
                                  }}
                                />
                              </div>

                              {/* Expiry countdown */}
                              <div className="mt-4 flex justify-between items-center text-xs">
                                <div
                                  className={`flex items-center ${
                                    isExpired
                                      ? "text-red-500"
                                      : daysRemaining <= 3
                                      ? "text-red-500"
                                      : daysRemaining <= 7
                                      ? "text-amber-500"
                                      : "text-green-600"
                                  }`}
                                >
                                  <FaCalendarAlt className="w-3.5 h-3.5 mr-1.5" />
                                  {isExpired ? (
                                    <span className="font-medium">
                                      Expired on{" "}
                                      {expiryDate.toLocaleDateString()}
                                    </span>
                                  ) : (
                                    <span className="font-medium">
                                      {daysRemaining === 0
                                        ? "Expires today"
                                        : daysRemaining === 1
                                        ? "Expires tomorrow"
                                        : `${daysRemaining} days remaining`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          )}

          {activeTab === "partners" && <PartnerManagement />}
        </div>
        <CreateYachtBookingModal
          isOpen={isRentalModalOpen}
          onClose={() => setIsRentalModalOpen(false)}
          onSuccess={() => {
            setIsRentalModalOpen(false);
            fetchData();

            Swal({
              title: "Yacht Booking Created",
              text: "The yacht booking has been successfully created.",
              icon: "success",
            });
          }}
        />
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
